import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getBrandingSettings, uploadBrandingLogo } from '../services/api';
import defaultLogoAsset from '../assets/villa-experience-logo-small.png';

const BrandingContext = createContext({
  logoUrl: null,
  rawLogoUrl: null,
  defaultLogoUrl: null,
  lastUpdated: null,
  loading: true,
  error: null,
  refresh: async () => {},
  uploadLogo: async () => {}
});

// Warm the browser cache before the URL is handed to consumers: several
// <img crossOrigin="anonymous"> instances mount simultaneously (Sidebar
// renders the logo twice, plus Navbar/Dashboard), and parallel cold fetches
// of the same URL don't coalesce — preloading first makes them cache hits.
const preloadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = resolve;
  img.onerror = resolve;
  img.src = src;
});

export function BrandingProvider({ children }) {
  const [state, setState] = useState({
    logoUrl: null,
    defaultLogoUrl: defaultLogoAsset,
    lastUpdated: null,
    loading: true,
    error: null
  });

  const refresh = useCallback(async () => {
    // Don't try to fetch branding if not logged in
    const token = localStorage.getItem('token');
    if (!token) {
      setState(prev => ({ ...prev, loading: false, error: null }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await getBrandingSettings();
      const data = response?.data || {};
      await preloadImage(data.logoUrl || defaultLogoAsset);
      setState(prev => ({
        ...prev,
        logoUrl: data.logoUrl || null,
        // Keep the bundled default rather than the API's defaultLogoUrl
        // (/assets/logo-villaexperience-gold.png): that public/ path only
        // exists if the deployed build is new enough to contain it — prod
        // 404'd on it for months because of exactly that deploy lag. The
        // Vite import is content-hashed, build-verified, and preloaded.
        defaultLogoUrl: prev.defaultLogoUrl,
        lastUpdated: data.updatedAt || null,
        loading: false,
        error: null
      }));
    } catch (error) {
      // Silent fail for connection errors, auth errors, or 404 (endpoint may not exist)
      const isExpectedError =
        error.code === 'ECONNREFUSED' ||
        error.code === 'ERR_NETWORK' ||
        error.response?.status === 401 ||
        error.response?.status === 404;

      if (!isExpectedError) {
        console.error('Error loading branding settings:', error);
      }
      await preloadImage(defaultLogoAsset);
      setState(prev => ({
        ...prev,
        loading: false,
        error: isExpectedError ? null : error
      }));
    }
  }, []);

  useEffect(() => {
    let isMounted = true
    
    const loadBranding = async () => {
      await refresh()
      if (!isMounted) return
    }
    
    loadBranding()
    
    return () => {
      isMounted = false
    }
  }, [refresh]);

  const uploadLogo = useCallback(async (file) => {
    if (!file) {
      throw new Error('No file provided for upload');
    }
    const formData = new FormData();
    formData.append('logo', file);
    const response = await uploadBrandingLogo(formData);
    const data = response?.data || {};
    setState(prev => ({
      ...prev,
      logoUrl: data.logoUrl || prev.logoUrl,
      lastUpdated: data.updatedAt || new Date().toISOString(),
      error: null
    }));
    return data;
  }, []);

  const value = useMemo(() => {
    // Hold the logo back until the branding fetch settles: rendering the
    // default first and swapping to the custom logo aborts the in-flight
    // default request on every mount. Consumers show their "VE" placeholder
    // block while this is null.
    const resolvedLogoUrl = state.loading ? null : (state.logoUrl || state.defaultLogoUrl);
    return {
      logoUrl: resolvedLogoUrl,
      rawLogoUrl: state.logoUrl,
      defaultLogoUrl: state.defaultLogoUrl,
      lastUpdated: state.lastUpdated,
      loading: state.loading,
      error: state.error,
      refresh,
      uploadLogo
    };
  }, [state, refresh, uploadLogo]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}













